import { createContext, useState, useContext, type Dispatch, type SetStateAction, type ReactNode } from 'react';
import { ValidationContext } from './ValidationContext';
import { ValidationMinMaxContext } from './ValidationMinMaxContext';
import { getProjects } from '../api/projectApi';
import { AuthContext } from '../app/authContext';

export interface ProjectFormCoordinates {
  lat: string;
  lng: string;
}

export interface ProjectFormData {
  name: string;
  description: string;
  analysisType: string;
  country: string;
  address: string;
  coordinates: ProjectFormCoordinates;
  municipality: string;
  street: string;
  number: string;
  internalSpace: number;
  terraceSpace: number;
  numberOfTerrace: number;
  yearlyHeatingPeakMonth: number;
  yearlyCoolingPeakMonth: number;
  dailyElectricityDemandPeak: string;
  dailyCoolingDemandPeak: string;
  photovoltaicSurfaceForSite: number;
  thermalCollectorsSurface: number;
  dailyOccupationPeak: string;
  sizeOfTheArea: number;
  photovoltaicSurfaceForLand: number;
  numberOfBuildings: number;
  buildingUse: 'Residential' | 'Office';
  grossFloorSurface: number | null;
  numberOfFloors: number | null;
}

export interface ProjectContextValue {
  project: ProjectFormData;
  setProject: Dispatch<SetStateAction<ProjectFormData>>;
  clearProjectFormData: (projectsCount?: number) => Promise<void>;
  handleChangeAnalysisType: (name: string, value: string, valid?: boolean) => void;
}

export const ProjectContext = createContext<ProjectContextValue>({} as ProjectContextValue);

const projectData: ProjectFormData = {
  name: '',
  description: '',
  analysisType: 'site',
  country: 'Switzerland',
  address: '',
  coordinates: {
    lat: '',
    lng: '',
  },
  municipality: '',
  street: '',
  number: '',
  internalSpace: 10.0,
  terraceSpace: 10.0,
  numberOfTerrace: 1,
  yearlyHeatingPeakMonth: 1,
  yearlyCoolingPeakMonth: 7,
  dailyElectricityDemandPeak: '18',
  dailyCoolingDemandPeak: '14',
  photovoltaicSurfaceForSite: 10.0,
  thermalCollectorsSurface: 10.0,
  dailyOccupationPeak: '7-18',
  sizeOfTheArea: 1.0,
  photovoltaicSurfaceForLand: 1.0,
  numberOfBuildings: 1,
  buildingUse: 'Residential',
  grossFloorSurface: null,
  numberOfFloors: null,
};

interface ProjectContextProviderProps {
  children: ReactNode;
}

const ProjectContextProvider = (props: ProjectContextProviderProps) => {
  const authContext = useContext(AuthContext);
  const { setIsProjectMunicipalityValid, setProjectMunicipalityErrorMessage } = useContext(ValidationContext);
  const { clearValidationProjectMinMaxInputs } = useContext(ValidationMinMaxContext);
  const activeOrganizationId = authContext.user?.session?.activeOrganizationId;

  const [project, setProject] = useState<ProjectFormData>(projectData);

  // For dynamic project name
  const clearProjectFormData = async (projectsCount?: number) => {
    setProject(projectData);

    // If caller already knows how many projects exist, skip fetching again
    if (typeof projectsCount === 'number') {
      setProject((prevProject) => ({
        ...prevProject,
        name: `Project ${projectsCount + 1}`,
      }));
      return;
    }

    if (!activeOrganizationId) {
      setProject((prevProject) => ({
        ...prevProject,
        name: 'Project 1',
      }));
      return;
    }

    const projects = await getProjects();
    const count = Array.isArray(projects) ? projects.length : 0;

    setProject((prevProject) => ({
      ...prevProject,
      name: `Project ${count + 1}`,
    }));
  };

  const handleChangeAnalysisType = (_name: string, value: string, _valid?: boolean) => {
    setProject({
      ...project,
      description: project.description ?? '',
      analysisType: value,
      // country: '',
      address: '',
      coordinates: {
        lat: '',
        lng: '',
      },
      municipality: '',
      street: '',
      number: '',
      internalSpace: 10.0,
      terraceSpace: 10.0,
      numberOfTerrace: 1,
      yearlyHeatingPeakMonth: 1,
      yearlyCoolingPeakMonth: 7,
      dailyElectricityDemandPeak: '18',
      dailyCoolingDemandPeak: '14',
      photovoltaicSurfaceForSite: 10.0,
      thermalCollectorsSurface: 10.0,
      dailyOccupationPeak: '7-18',
      sizeOfTheArea: 1.0,
      photovoltaicSurfaceForLand: 1.0,
      numberOfBuildings: 1,
      buildingUse: 'Residential',
      grossFloorSurface: null,
      numberOfFloors: null,
    });

    if (value === 'land') {
      setIsProjectMunicipalityValid(true);
      setProjectMunicipalityErrorMessage('');
    }

    if (value === 'site') {
      setIsProjectMunicipalityValid(false);
      setProjectMunicipalityErrorMessage('Please enter valid latitude and longitude!');
    }

    clearValidationProjectMinMaxInputs();
  };

  return (
    <ProjectContext.Provider
      value={{
        project,
        setProject,
        clearProjectFormData,
        handleChangeAnalysisType,
      }}
    >
      {props.children}
    </ProjectContext.Provider>
  );
};

export default ProjectContextProvider;
